/**
 * @title Editor component for the Ciao Prolog playground
 * 
 * Editor component based on the Monaco editor.
 *
 * @author The Ciao Development Team
 */

// ===========================================================================
// * Dynamically load dependencies

// TODO: not very fast but robust enough; better ways?
/* NOTE: Do not use res_URL for third parties, only urlPREFIX */
window.require = { paths: { vs: urlPREFIX+'/node_modules/monaco-editor/min/vs' } };
(function() {
  importCSS(urlPREFIX+'/node_modules/monaco-editor/min/vs/editor/editor.main.css');
  // monaco
  importScript(urlPREFIX+'/node_modules/monaco-editor/min/vs/loader.js');
  importScript(urlPREFIX+'/node_modules/monaco-editor/min/vs/editor/editor.main.js');
  importScript(urlPREFIX+'/node_modules/monaco-editor/min/vs/editor/editor.main.nls.js');
  // Ciao syntax for monaco
  importScript(res_URL('/playground/js/syntax/ciao-language.js'));
  importScript(res_URL('/playground/js/syntax/ciao-toplevel-language.js'));
})();

// ---------------------------------------------------------------------------

/** Editor theme for each UI theme */
const editor_theme = {
  "light": "ciao-light",
  "dark": "ciao-dark"
};

// ---------------------------------------------------------------------------
// * Editor component abstraction (both for code and toplevel)

class PGEditor {
  constructor(ed) {
    this.ed = ed; // monaco editor component
  }
  has_focus() {
    return this.ed.hasWidgetFocus();
  }
  set_focus() {
    this.ed.focus();
  }
  update_dimensions() {
    this.ed.layout();
  }
  on_change_content(m) {
    this.ed.getModel().onDidChangeContent(m);
  }
  set_key(k, m) {
    this.ed.addCommandFocused(tr_key(k), m);
  }
  get_value() {
    return this.ed.getModel().getValue();
  }
  set_value(val) {
    this.ed.getModel().setValue(val);
  }
  set_kind(kind) { // TODO: abstraction of lang, mode, etc.
    let lang = editor_kind_to_lang(kind);
    monaco.editor.setModelLanguage(this.ed.getModel(), lang);
  }
  // TODO: simpler set_opts
  set_read_only(value) {
    this.ed.updateOptions({ readOnly: value });
  }
  set_line_numbers(value) {
    value = value ? "on" : "off";
    this.ed.updateOptions({ lineNumbers: value });
  }
  //
  get_line_num() {
    return this.ed.getPosition().lineNumber;
  }
  get_line_count() {
    return this.ed.getModel().getLineCount();
  }
  get_line_content(line) {
    return this.ed.getModel().getLineContent(line);
  }
  get_line_length(line) {
    return this.ed.getModel().getLineLength(line);
  }
  get_position() {
    let p = this.ed.getPosition();
    return { col: p.column, row: p.lineNumber };
  }
  set_position(col, row) {
    this.ed.setPosition({ lineNumber: row, column: col });
  }
  //
  insert_text(text) { // (insert text even if the editor was set to read-only)
    var ro = this.ed.getRawOptions().readOnly;
    if (ro) this.ed.updateOptions({ readOnly: false }); // TODO: better way?
    this.ed.trigger('keyboard', 'type', {
      text: text
    });
    if (ro) this.ed.updateOptions({ readOnly: ro });
  }
  remove_range(col1, row1, col2, row2) {
    this.ed.executeEdits('', [{
      range: new monaco.Range(row1, col1, row2, col2),
      text: null
    }]);
  }
  //
  reveal_line(line) {
    this.ed.revealLine(line);
  }
  //
  set_markers(file, msgs) { // TODO: remove 'file'?
    let markers = create_markers(file, msgs); // create monaco markers
    monaco.editor.setModelMarkers(this.ed.getModel(), 'errors', markers);
  }
  mark_srcdbg_info(cl, info) {
    let range = editor_srcdbg_info_to_range(this, info);
    editor_mark_srcdbg_info(this, cl, range);
  }
  unmark_srcdbg_info() {
    editor_mark_srcdbg_info(this, null, null);
  }

  /** Extracts content block between specific delimiter (LPTP support) */
  extract_delimited_block(delimiter, fromEnd = true) {
    const ed = this.ed;
    const lines = ed.getModel().getLinesContent();
    let startIndex = -1;
    let endIndex = -1;

    if (fromEnd) {
      // Search from end to find the last block
      for (let i = lines.length - 1; i >= 0; i--) {
        if (lines[i].trim() === delimiter) {
          if (endIndex === -1) {
            endIndex = i;
          } else {
            startIndex = i;
            break;
          }
        }
      }
    } else {
      // Search from beginning
      for (let i = 0; i < lines.length; i++) {
        if (lines[i].trim() === delimiter) {
          if (startIndex === -1) {
            startIndex = i;
          } else {
            endIndex = i;
            break;
          }
        }
      }
    }

    if (startIndex === -1 || endIndex === -1 || endIndex <= startIndex + 1) {
      return null;
    }

    return lines.slice(startIndex + 1, endIndex).join("\n").trim();
  }

  /** Gets selected text and if nothing is selected tries match patters around cursor (LPTP support) */
  get_text_at_cursor(patterns = []) {
    const ed = this.ed;
    const selection = ed.getSelection();
    
    // If there's a selection, return it
    if (selection && !selection.isEmpty()) {
      return ed.getModel().getValueInRange(selection).trim();
    }

    // Otherwise, try to match patterns around cursor
    const position = ed.getPosition();
    const line = ed.getModel().getLineContent(position.lineNumber);
    const cursorPos = position.column - 1;

    for (const pattern of patterns) {
      let match;
      const regex = new RegExp(pattern.source, pattern.flags);
      
      while ((match = regex.exec(line)) !== null) {
        const matchStart = match.index;
        const matchEnd = regex.lastIndex;
        
        if (cursorPos >= matchStart && cursorPos <= matchEnd) {
          return match[0]; // Return full match or specific group as needed
        }
      }
    }

    return null;
  }

  /** Gets predicate name around cursor position (LPTP support) */
  get_predicate_name_at_cursor() {
    const ed = this.ed;
    const selection = ed.getSelection();
    
    if (selection && !selection.isEmpty()) {
      return ed.getModel().getValueInRange(selection).trim();
    }

    const position = ed.getPosition();
    const line = ed.getModel().getLineContent(position.lineNumber);
    const cursor = position.column - 1;

    // Match predicate calls like: foo(...), bar(...), tree:bar(...)
    const callRegex = /\b([a-zA-Z_][a-zA-Z0-9_]*)(?=\s*\()/g;
    let match;
    while ((match = callRegex.exec(line)) !== null) {
      const start = match.index;
      const end = callRegex.lastIndex;
      if (cursor >= start && cursor <= end + 1) {
        return match[1];
      }
    }

    // Match module-qualified atoms like: tree:bar
    const modRegex = /\b([a-zA-Z_][a-zA-Z0-9_]*)\:([a-zA-Z_][a-zA-Z0-9_]*)/g;
    while ((match = modRegex.exec(line)) !== null) {
      const fullStart = match.index;
      const modStart = match.index;
      const modEnd = modStart + match[1].length;
      const functorStart = modEnd + 1;
      const functorEnd = functorStart + match[2].length;

      if (cursor >= modStart && cursor <= modEnd) {
        return match[1]; // inside module → return module name
      }
      if (cursor >= functorStart && cursor <= functorEnd) {
        return match[2]; // inside functor → return functor
      }
    }

    return null;
  }

  /** Transforms current line using arbitrary transform (LPTP support) */
  transform_current_line(transformer) {
    const ed = this.ed;

    const position = ed.getPosition();
    const lineNumber = position.lineNumber;
    const lineContent = ed.getModel().getLineContent(lineNumber);
    
    const newContent = transformer(lineContent, position);
    
    const model = ed.getModel();
    const range = new monaco.Range(
      lineNumber, 
      1, 
      lineNumber, 
      model.getLineMaxColumn(lineNumber)
    );
    
    model.pushEditOperations([], [{
      range: range,
      text: newContent
    }], () => null);
  }
}

var emacs_key_to_monaco = undefined;
var emacs_modif_to_monaco = undefined;

function ensure_init_key_tbl() {
  if (emacs_key_to_monaco !== undefined) return;
  const KM = monaco.KeyMod;
  const KC = monaco.KeyCode;
  emacs_key_to_monaco = {
    // Letters
    'a': KC.KeyA, 'b': KC.KeyB, 'c': KC.KeyC,
    'd': KC.KeyD, 'e': KC.KeyE, 'f': KC.KeyF,
    'g': KC.KeyG, 'h': KC.KeyH, 'i': KC.KeyI,
    'j': KC.KeyJ, 'k': KC.KeyK, 'l': KC.KeyL,
    'm': KC.KeyM, 'n': KC.KeyN, 'o': KC.KeyO,
    'p': KC.KeyP, 'q': KC.KeyQ, 'r': KC.KeyR,
    's': KC.KeyS, 't': KC.KeyT, 'u': KC.KeyU,
    'v': KC.KeyV, 'w': KC.KeyW, 'x': KC.KeyX,
    'y': KC.KeyY, 'z': KC.KeyZ,
    // Numbers
    '0': KC.Digit0, '1': KC.Digit1,
    '2': KC.Digit2, '3': KC.Digit3,
    '4': KC.Digit4, '5': KC.Digit5,
    '6': KC.Digit6, '7': KC.Digit7,
    '8': KC.Digit8, '9': KC.Digit9,
    // Punctuation and symbols
    '-': KC.Minus,
    '=': KC.Equal,
    '[': KC.BracketLeft,
    ']': KC.BracketRight,
    '\\': KC.Backslash,
    ';': KC.Semicolon,
    '\'': KC.Quote,
    ',': KC.Comma,
    '.': KC.Period,
    '/': KC.Slash,
    '`': KC.Backquote,
    'SPC': KC.Space,
    // ' ': KC.Space, // Accept plain space too // TODO: problematic...
    // Emacs special keys
    'RET': KC.Enter,
    'LFD': KC.Enter,
    'ESC': KC.Escape,
    'DEL': KC.Backspace,
    'BS': KC.Backspace,
    'TAB': KC.Tab,
    'INS': KC.Insert,
    'HOME': KC.Home,
    'END': KC.End,
    'PGUP': KC.PageUp,
    'PGDN': KC.PageDown,
    'LEFT': KC.LeftArrow,
    'RIGHT': KC.RightArrow,
    'UP': KC.UpArrow,
    'DOWN': KC.DownArrow,
    // Function keys
    'F1': KC.F1,  'F2': KC.F2,
    'F3': KC.F3,  'F4': KC.F4,
    'F5': KC.F5,  'F6': KC.F6,
    'F7': KC.F7,  'F8': KC.F8,
    'F9': KC.F9,  'F10': KC.F10,
    'F11': KC.F11,'F12': KC.F12
  };
  emacs_modif_to_monaco = {
    'C': KM.WinCtrl,
    'M': KM.Alt,
    'S': KM.Shift
  };
}

function tr_key(k) {
  ensure_init_key_tbl();
  const keys = k.trim().split(/\s+/).map(part => {
    const mods = part.split('-');
    const keyStr = mods.pop(); // last element is the key name
    let key = emacs_key_to_monaco[keyStr];
    if (!key) throw new Error(`Unknown key: ${keyStr}`);
    for (const m of mods) {
      const bit = emacs_modif_to_monaco[m];
      if (!bit) throw new Error(`Unknown modifier: ${m}`);
      key |= bit;
    }
    return key;
  });
  // if (keys.length == 2) {
  //   const KM = monaco.KeyMod;
  //   return KM.chord(keys[0], keys[1]);
  // } else if (keys.length == 1) {
  //   return keys[0];
  // } else {
  //   throw new Error(`Unknown chord: ${k}`);
  // }
  return keys;
}

var pg_editor_num = 0;

/* Create a playground editor component (file or toplevel). If
   opts.autoresize==true then the size will dinamically change with
   contents (for lpdoc-runnable). */
export function create_pg_editor(container, text, kind, opts) {
  const theme = editor_theme[get_actual_theme()];
  let maxh = 500;
  let minh = 0;
  const ops = {
    value: text,
    scrollBeyondLastLine: false,
    /* (same font size and family than in lpdoc.css) */
    fontSize: "14px",
    lineHeight: "17px", /* same result as in lpdoc.css */ 
    fontFamily: "SFMono-Regular,Consolas,Liberation Mono,Menlo,monospace",
    minimap: {
      enabled: false
    },
    "bracketPairColorization.enabled": false,
    theme: theme,
    autoClosingBrackets: false,
    overviewRulerLanes: 0,
    renderLineHighlight: 'none',
    automaticLayout: false /* Do not use! Expensive! */
  };
  if (kind == 'toplevel') {
    Object.assign(ops, {
      language: 'toplevel',
      lineNumbers: 'off',
      folding: 'false',
      autoIndent: 'none',
      guides: {
        indentation: false
      },
      wordWrap: 'on'
    });
    maxh = 160;
  } else {
    var lang = editor_kind_to_lang(kind);
    Object.assign(ops, {
      language: lang,
      lineNumbersMinChars: 3
    });
  }
  if (opts.autoresize === true) {
    Object.assign(ops, {
      scrollbar: {
        alwaysConsumeMouseWheel: false // TODO: good for automatic updateHeight
      }
    });
  }
  const ed = monaco.editor.create(container, ops);
  if (opts.autoresize === true) {
    //container.style.height = '300px';
    let ignoreEvent = false;
    const updateHeight = () => {
      const w = container.clientWidth;
      const h = Math.max(minh, Math.min(maxh, ed.getContentHeight()));
      if (opts.noshrink === true) minh = h; // Force minh to prevent shrinking
      //container.style.width = `${width}px`;
      container.style.height = `${h}px`;
      try {
        ignoreEvent = true;
        ed.layout({ width: w, height: h });
      } finally {
        ignoreEvent = false;
      }
    };
    ed.onDidContentSizeChange(updateHeight);
    ed.onDidLayoutChange(updateHeight); /* when scrollbars (dis)appear */
    updateHeight();
  }
  {
    // workaround regression in Monaco 0.32.0 https://github.com/microsoft/monaco-editor/issues/2947
    // This creates a context key that is enabled only when the editor is focused.
    const focused_key_name = `__isEditorFocused-${pg_editor_num}`;
    pg_editor_num++;
    //
    ed.addCommandFocused = (keybinding, handler) => {
      return ed.addCommand(keybinding, handler, focused_key_name);
    };
    const ctxkey = ed.createContextKey(focused_key_name, false);
    ed.onDidBlurEditorWidget(() => ctxkey.set(false));
    ed.onDidFocusEditorText(() => ctxkey.set(true));
  }
  let editor = new PGEditor(ed);
  editor_set_emacs_bindings(editor);
  return editor;
}

// ---------------------------------------------------------------------------
// * Update editor theme

export function editor_update_theme() {
  if (typeof monaco === 'undefined') return; /* monaco not loaded yet */
  monaco.editor.setTheme(editor_theme[get_actual_theme()]);
}

// ---------------------------------------------------------------------------
// * Editor languages

function editor_kind_to_lang(kind) {
  if (kind == 'markdown') {
    return 'markdown';
  } else if (kind == 'prolog') {
    return 'ciao-prolog';
  } else {
    throw new Error("unknown editor kind");
  }
}

// ---------------------------------------------------------------------------
// * Emacs style bindings

/* Set some Emacs edition key bindings */
function editor_set_emacs_bindings(editor) {
  /*
    Bindings availabe in monaco by default (before this patch):
    - Move cursor to line start (C-a)
    - Move cursor to line end (C-e)
    - Move cursor up (C-p)
    - Move cursor down (C-n)
    - Move cursor right (C-f)
    - Move cursor left (C-b)
    - Delete left character (C-h) // TODO: not emacs!
    - Kill selected text (C-k)
    - Insert line below (C-o) // TODO: not emacs!
    - Move to line (Cmd-g) // TODO: not emacs!
  */
  [
    ["C-s", 'actions.find'], // Find
    ["C-r", 'actions.find'], // Find
    ["C-x C-p", 'editor.action.selectAll'], // Select all
    ["C-m", 'editor.action.insertLineAfter'], // Insert line after
    ["C-z", 'undo'], // Undo
    ["C-x u", 'undo'], // Undo
    ["C-x C-u", 'editor.action.transformToUppercase'], // TODO: different in emacs?
    ["C-x C-l", 'editor.action.transformToLowercase'], // TODO: different in emacs?
    ["M-d", 'deleteWordRight'], // Delete right word (M-d) // TODO: Alt vs Escape vs Command?
    ["M-v", 'cursorPageUp'], // Page Up
    ["C-v", 'cursorPageDown'], // Page Down
    ["M-DEL", 'deleteWordLeft'], // Delete left word (M-backspace)
    ["M-;", 'editor.action.commentLine'], // Comment line (M-;) // TODO: not the same as in emacs!
  ].forEach(([key, handlerId]) => editor.set_key(key, () => {
    editor.ed.trigger('editor', handlerId);
  }));
}

// ---------------------------------------------------------------------------
// * Error/warning markers

// Constants to distinguish between errors and warnings
const ERROR = 0;
const WARNING = 1;

/**
 * Generates an array containing all the needed markers for Monaco (only for the input file).
 * @param {MsgObject} msgs - Object containing warnings and errors from the file.
 * @returns {monaco.editor.IMarkerData[]} - Array containing markers for Monaco.
 */
function create_markers(file, msgs) {
  let markers = [];
  add_markers(file, msgs.errors, markers, ERROR);
  add_markers(file, msgs.warnings, markers, WARNING);
  return markers;
}

/**
 * Loops through the elements of the `array` to add the content to `markers` in 
 * the right format.
 * @param {Error[]|Warning[]} array - Array of Error or Warning with the elements to loop.
 * @param {monaco.editor.IMarkerData[]} markers - Array of markers to be filled.
 * @param {number} type - Especifies whether it is an Error or a Warning.
 */
function add_markers(file, array, markers, type) {
  // loop all array
  array.forEach(e => {
    if (e.file !== undefined && e.file !== file) return; // not this file
    if (e.lines === undefined) return; // general error (no lines defined)

    // lines parameter defined as: 'startLine-endLine'
    let startLine = e.lines.slice(0, e.lines.indexOf('-'));
    let endLine = e.lines.slice(e.lines.indexOf('-') + 1);

    // add to the markers array
    markers.push({
      severity: (type === ERROR) ? monaco.MarkerSeverity.Error : monaco.MarkerSeverity.Warning,
      startLineNumber: parseInt(startLine),
      startColumn: 0,
      endLineNumber: parseInt(endLine),
      endColumn: 1000,
      message: e.msg
    });
  });
};

// ---------------------------------------------------------------------------
// * Debugger support

/** Extract range from srcdbg_info */
// TODO: this is an approximation similar to what is implemented in
//   ciao-debugger.el, we'd need to identify goal locations (at
//   compile time).
function editor_srcdbg_info_to_range(editor, info) {
  let ed = editor.ed;
  const model = ed.getModel();
  let search_range = new monaco.Range(info.ln0, 1, info.ln1+1, 1);
  let text = model.getValueInRange(search_range);
  // Tokenize the search region and find matches.
  // See debugger_tr.pl for the meaning of num (it included substrings)
  let tokens = monaco.editor.tokenize(text, 'ciao-prolog');
  var cnt = 0;
  for (let row = 0; row < tokens.length; row++) {
    let line = model.getLineContent(info.ln0+row);
    for (let tk = 0; tk < tokens[row].length; tk++) {
      var offset = tokens[row][tk].offset;
      var next_offset;
      if (tk == tokens[row].length-1) {
        next_offset = line.length;
      } else {
        next_offset = tokens[row][tk+1].offset;
      }
      let txt = line.slice(offset, next_offset);
      // console.log(`(${row}) tk ${tk} ${offset} ${txt} ${tokens[row][tk].type}`);
      let type = tokens[row][tk].type;
      switch(type) {
      case 'predefined.operator.ciao-prolog': break;
      case 'type.identifier.ciao-prolog': break; // TODO: fix name (should be vars)
      case 'string-single.ciao-prolog': break; // TODO: fix name (should be quoted atom)
      case 'operator.ciao-prolog': break;
      case 'source.ciao-prolog': 
        // amend wrong tokenizer // TODO: fixme
        if (txt.endsWith('.')) {
          txt = txt.slice(0,-1);
          next_offset--;
        }
        break;
      case 'pred-name.ciao-prolog': break;
      default: continue; // skip this token
      }
      // Increment for each (non-overlapping) occurrence in 'txt'
      // (this is what we require for ciao-debugger.el)
      var pos = 0;
      while (pos < txt.length) {
        pos = txt.indexOf(info.pred, pos);
        if (pos === -1) break;
        cnt++; // found one occurrence
        pos += info.pred.length; // move forward
      }
      if (cnt === info.num) {
        return new monaco.Range(info.ln0+row, offset+1, info.ln0+row, next_offset+1);
      }
    }
  }
  return search_range;
}

function editor_mark_srcdbg_info(editor, cl, range) {
  let ed = editor.ed;
  let decs = [];
  if (cl !== null) {
    // // mark the whole line // TODO: not working? wrong monaco version?
    decs.push({
      range: new monaco.Range(range.startLineNumber, 1, range.startLineNumber, 1),
      options: {
        isWholeLine: true,
        className: cl+"-bg"
      }
    });
    // and the token
    decs.push({
      range: range,
      options: {
        isWholeLine: false,
        // after: { content: "..." }, // TODO: mark the goal, add bindings?
        inlineClassName: cl
      }
    });
    ed.revealLine(range.startLineNumber);
  }
  if (editor.dbg_decorations !== undefined) {
    editor.dbg_decorations.clear();
    editor.dbg_decorations = undefined;
  }
  if (decs.length > 0) {
    editor.dbg_decorations = ed.createDecorationsCollection(decs);
  }
}
